import React from "react";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Users,
  Star,
} from "lucide-react";
import { config } from "../../config/environment";

interface UserProfileDetailsProps {
  user: any;
  tracking?: any;
}

const UserProfileDetails: React.FC<UserProfileDetailsProps> = ({
  user,
  tracking,
}) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const toDate = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "number") {
      // seconds vs ms
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === "string") {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const formatDateTime = (value: any) => {
    const d = toDate(value);
    if (!d) return "N/A";
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Avatar helpers (same behavior as header)
  const getImageUrl = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${config.apiUrl}${url.startsWith("/") ? url : `/${url}`}`;
  };

  const parseAvatarScheme = (value?: string) => {
    if (!value || !value.startsWith("avatar:")) return null;
    try {
      const payload = value.replace("avatar:", "");
      const [color, initials] = payload.split(":");
      return {
        color: color || "#CBD5E1",
        initials: (initials || "U").substring(0, 2).toUpperCase(),
      };
    } catch {
      return null;
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">User Details</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            Personal Information
          </h3>

          <div className="space-y-3">
            {/* Avatar preview */}
            <div className="flex items-center gap-3">
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center overflow-hidden"
                style={{
                  backgroundColor:
                    parseAvatarScheme(user?.profilePicture)?.color || "#E5E7EB",
                }}
              >
                {(() => {
                  const avatar = parseAvatarScheme(user?.profilePicture);
                  const imgUrl = getImageUrl(user?.profilePicture);
                  if (avatar) {
                    return (
                      <span className="text-white text-base font-semibold">
                        {avatar.initials}
                      </span>
                    );
                  }
                  if (imgUrl && !user?.profilePicture?.startsWith("avatar:")) {
                    return (
                      <img
                        src={imgUrl}
                        alt={user?.fullName || "User"}
                        className="h-12 w-12 object-cover"
                      />
                    );
                  }
                  return null;
                })()}
              </div>
              <div>
                <p className="text-sm text-gray-500">Avatar</p>
                <p className="text-gray-900">
                  {user?.fullName ||
                    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                    "User"}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-gray-900">{user?.email || "N/A"}</p>
              </div>
            </div>

            <div className="flex items-center">
              <Users className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">User ID</p>
                <p className="text-gray-900">{user?._id || "N/A"}</p>
              </div>
            </div>

            <div className="flex items-center">
              <Phone className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-gray-900">{user?.phoneNumber || "N/A"}</p>
              </div>
            </div>

            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="text-gray-900">{formatDate(user?.dateOfBirth)}</p>
              </div>
            </div>

            {user?.gender && (
              <div className="flex items-center">
                <Users className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Gender</p>
                  <p className="text-gray-900 capitalize">{user.gender}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location & Account Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            Location & Account
          </h3>

          <div className="space-y-3">
            <div className="flex items-start">
              <MapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="text-gray-900">
                  {user?.location?.address || "N/A"}
                </p>
                {user?.location?.coordinates && (
                  <p className="text-xs text-gray-500 mt-1">
                    Coordinates: {user.location.coordinates.join(", ")}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Account Created</p>
                <p className="text-gray-900">{formatDate(user?.createdAt)}</p>
              </div>
            </div>

            <div className="flex items-center">
              <Clock className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Last Login</p>
                <p className="text-gray-900">
                  {formatDateTime(
                    user?.lastLoginAt ||
                      tracking?.lastLoginDate ||
                      tracking?.lastSeen ||
                      user?.updatedAt
                  )}
                </p>
              </div>
            </div>

            {tracking?.currentVersion && (
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Current Version</p>
                  <p className="text-gray-900">
                    v{tracking.currentVersion?.version || "N/A"} (
                    {tracking.currentVersion?.platform || "unknown"})
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Interests */}
      {user?.interests && user.interests.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Interests</h3>
          <div className="flex flex-wrap gap-2">
            {user.interests.map((interest: string, index: number) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
              >
                <Star className="h-3 w-3 mr-1" />
                {interest}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDetails;
